// Which currency a fee is collected in, which currency a POLICY prices,
// and which policies a given market could ever be charged under
// (SM-503, rewritten for SM-510).
//
// ── the question this file answers ───────────────────────────────────
//
// A fee rate is a PERCENTAGE, and a percentage has no currency. What has
// a currency is the money the percentage is taken out of: settlement
// withholds a fee from the leg the cash moved on and never converts it
// (settlement-service channelpolicy.go, `FeeCurrency = TradeCurrency`).
// So the currency of a fee is the book's, and the side's:
//
//   Cash book → Gold, whichever side pays.
//   Game book → Red on a buy (the debit leg), Green on a sell, a
//               redemption or a payout (the credit leg).
//
// The mapping is read from MODE_CURRENCIES rather than restated, so a
// fourth currency joining a book is one line there and the filters,
// labels and previews here follow.
//
// ── what SM-510 changed ──────────────────────────────────────────────
//
// `fee_profiles` used to be keyed by (scope, scope_id) and nothing else,
// so ONE stored rate priced every book its markets ran: raise the
// platform taker fee to 3% and a Gold fill, a Red buy and a Green sell
// all started paying 3%. This module existed to say that out loud,
// because the console could not scope it away.
//
// It is no longer true. A policy is keyed by (scope, scope_id,
// currency) and prices exactly one currency; the resolution chain is
// walked within it, and a Gold policy never moves a Red fee. So the
// currency filter narrows the QUERY — the server returns one currency's
// platform default, category overrides and market overrides — instead of
// guessing from which books a scope's markets happen to have open.
//
// Two things survive from before, for different reasons:
//
//   LEGACY rows. Every policy written before the migration carries an
//   empty currency and still prices every book at its level, exactly as
//   it did, until an operator replaces that level. They are read-only
//   from the API's point of view and the console labels them as what
//   they are rather than pretending they belong to a currency.
//
//   Reach. Which books a market has open is still the right answer to
//   "can this market be charged in Red at all", which is what the create
//   form needs before it offers a market for a Red policy (AC 8). It is
//   no longer used to FILTER the policy list.
import { MODE_CURRENCIES, type TradingMode } from "@/lib/modeCurrencies";
import { isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import {
  localEffective,
  modeFlag,
  TRADING_MODES,
} from "@/components/markets/TradingModePanel";
import type { MarketItem } from "@/lib/types";

/** Books a fee can be charged on, in the order they render. */
export const FEE_BOOKS: readonly TradingMode[] = TRADING_MODES;

/** Which leg of a fill a fee was withheld from. */
export type FeeSide = "buy" | "sell";

/**
 * Every currency a fee on this book can be collected in — the book's own
 * currencies, because a fee is withheld from money that book moved.
 */
export function feeCurrencies(mode: TradingMode): CurrencyCode[] {
  return MODE_CURRENCIES[mode].currencies.filter(isCurrencyCode);
}

/**
 * The currency a fee on this side of a fill is collected in: the debit
 * leg for a buy, the payout leg for a sell.
 */
export function feeCurrencyForSide(mode: TradingMode, side: FeeSide): CurrencyCode {
  const code = side === "buy" ? MODE_CURRENCIES[mode].spend : MODE_CURRENCIES[mode].payout;
  return isCurrencyCode(code) ? code : "GOLD";
}

/**
 * The currency a REDEMPTION fee is collected in. A redemption credits
 * the payout leg, so its fee comes out of the payout currency — Green on
 * the Game book, not Red.
 */
export function redemptionFeeCurrency(mode: TradingMode): CurrencyCode {
  return feeCurrencyForSide(mode, "sell");
}

/** The books that can charge a fee in this currency — [] for none. */
export function booksForFeeCurrency(code: string): TradingMode[] {
  return FEE_BOOKS.filter((m) =>
    (MODE_CURRENCIES[m].currencies as readonly string[]).includes(code),
  );
}

/** Every currency any book charges fees in, in the contract's own order. */
export const FEE_CURRENCIES: readonly CurrencyCode[] = Array.from(
  new Set(FEE_BOOKS.flatMap(feeCurrencies)),
);

/**
 * The pre-SM-510 policies: one shared rate per level, priced into every
 * book, kept verbatim by migration 0036 so nothing repriced on deploy.
 *
 * On the wire the stored value is the empty string, which a query
 * string cannot distinguish from an absent parameter — hence the
 * explicit spelling, matching the server's own.
 */
export const LEGACY_FEE_CURRENCY = "legacy" as const;

/**
 * What the Fees pages carry in `?currency=`: one currency, the legacy
 * rows, or nothing — which means every currency.
 *
 * Absent is "all", not a default currency. On the day the migration
 * lands EVERY policy is a legacy row, so a page that defaulted to Gold
 * would open empty and read as "no policies configured". Each row names
 * its own currency now (AC 7), so a mixed list is legible in a way it
 * was not before.
 */
export type FeeCurrencyParam = CurrencyCode | typeof LEGACY_FEE_CURRENCY;

/**
 * Read `?currency=` out of untrusted text.
 *
 * Fail-OPEN, like every other picker-backed filter in the console: a
 * value this cannot read becomes "no filter" rather than a filter the
 * control cannot show. A table narrowed by a value the picker denies is
 * indistinguishable from an empty catalog.
 */
export function parseFeeCurrencySearch(raw: unknown): FeeCurrencyParam | undefined {
  if (raw === LEGACY_FEE_CURRENCY) return LEGACY_FEE_CURRENCY;
  return parseFeeCurrency(raw);
}

/**
 * Read a currency out of untrusted text, narrowed to currencies some
 * book actually charges fees in.
 */
export function parseFeeCurrency(raw: unknown): CurrencyCode | undefined {
  if (typeof raw !== "string" || !isCurrencyCode(raw)) return undefined;
  return booksForFeeCurrency(raw).length > 0 ? raw : undefined;
}

/** The search object for one selection — "all" carries no param. */
export function feeCurrencySearch(
  next: FeeCurrencyParam | undefined,
): { currency?: FeeCurrencyParam } {
  return next ? { currency: next } : {};
}

/** The currency a page is scoped to, or undefined for "all" / legacy. */
export function scopedCurrency(
  param: FeeCurrencyParam | undefined,
): CurrencyCode | undefined {
  return param && param !== LEGACY_FEE_CURRENCY ? param : undefined;
}

/**
 * The currency a WORKED EXAMPLE on a page should be quoted in.
 *
 * A legacy policy has no currency of its own — it prices all of them —
 * so an example under it falls back to Gold, the one currency every
 * market on the platform has always charged in (the Cash book is on
 * everywhere by backfill, the Game book off by default).
 */
export function exampleCurrency(
  policyCurrency: string | undefined,
): CurrencyCode {
  return policyCurrency && isCurrencyCode(policyCurrency) ? policyCurrency : "GOLD";
}

/** True for a policy row that predates SM-510 and still prices every book. */
export function isLegacyPolicy(policy: { currency?: string }): boolean {
  return !policy.currency;
}

/**
 * The sentence a legacy row carries wherever one is shown.
 *
 * One idea per sentence so the localized content bridge can extract it
 * (the SM-354 note in DESIGN.md), and no ALL-CAPS currency codes: the
 * names an operator configured are what the rest of the page shows.
 */
export const LEGACY_POLICY_NOTE =
  "This policy was set before fees were split by currency, so its one rate still prices every book its markets run. Saving a policy for a currency replaces it for that currency and leaves the others on this rate.";

// ── which books a scope's markets actually run ───────────────────────

/**
 * The books open on one market, from the two switches the console can
 * see.
 *
 * Availability is `event.<mode>_enabled AND market.<mode>_enabled`, so a
 * loaded event NARROWS the market's own flags. `event` is undefined in
 * two different situations and they are handled the same way on purpose:
 * a market with no parent event takes its own flag alone (the rule
 * TradingModePanel states), and a market whose event this page did not
 * load has an unread flag — which `reachForMarkets` reports through
 * `known` rather than resolving here. Reading an absent event as the
 * backfill default would close the Game book on every market that has no
 * event at all.
 *
 * Deliberately the two flags rather than the channels endpoint: that
 * endpoint is per-market and this runs over a whole catalog page. The
 * verdict it adds — the Game visibility rollout flag — gates what
 * TRADERS see, not what a fill would be charged, which is the question
 * a fee policy asks.
 */
export function openBooks(
  market: Pick<MarketItem, "cash_enabled" | "game_enabled">,
  event?: { cash_enabled?: boolean; game_enabled?: boolean },
): TradingMode[] {
  return FEE_BOOKS.filter((mode) =>
    event ? localEffective(event, market, mode) : modeFlag(market, mode),
  );
}

/** What a policy's markets can be charged in, and whether that is the whole story. */
export interface FeeReach {
  /** Books open on at least one of the scope's markets. */
  books: TradingMode[];
  /** Currencies those books charge fees in. */
  currencies: CurrencyCode[];
  /** How many markets the reach was computed over. */
  markets: number;
  /**
   * False when the scope covers markets this page did not load, so the
   * currencies above are a floor rather than the answer. A filter must
   * not hide a row on an unknown, and a label must not claim one.
   */
  known: boolean;
}

export const EMPTY_REACH: FeeReach = Object.freeze({
  books: [],
  currencies: [],
  markets: 0,
  known: false,
});

/**
 * The reach of a set of markets: every book any of them has open, and
 * every currency those books charge in.
 *
 * A union, not an intersection. A category override covering one Cash
 * market and one Game market prices Gold AND Red and Green fills, and an
 * operator editing it is moving all three.
 */
export function reachForMarkets(
  markets: readonly Pick<MarketItem, "market_id" | "event_id" | "cash_enabled" | "game_enabled">[],
  eventFlags?: ReadonlyMap<string, { cash_enabled?: boolean; game_enabled?: boolean }>,
  known = true,
): FeeReach {
  const books = new Set<TradingMode>();
  // A market whose parent event is outside the loaded window has one of
  // the two switches unread, and the union below is then a guess rather
  // than an answer — which is exactly what `known` exists to say.
  let eventsRead = true;
  for (const m of markets) {
    const event = m.event_id ? eventFlags?.get(m.event_id) : undefined;
    if (m.event_id && !event) eventsRead = false;
    for (const mode of openBooks(m, event)) books.add(mode);
  }
  const ordered = FEE_BOOKS.filter((m) => books.has(m));
  return {
    books: ordered,
    currencies: Array.from(new Set(ordered.flatMap(feeCurrencies))),
    markets: markets.length,
    known: known && eventsRead,
  };
}

/**
 * Whether a redemption fee written in this currency is ever actually
 * charged, and the sentence that says so.
 *
 * A redemption credits the PAYOUT leg, so its fee is carved from the
 * payout currency: Gold on the Cash book, Green on the Game book. Red is
 * the debit leg and never pays a redemption fee at all.
 *
 * And Green does not either, yet: REDEMPTION FEE POLICY v1 (SM-432)
 * charges nothing on a Game redemption, and settlement short-circuits
 * those before it resolves a rate. The rate is still stored and still
 * the number that would apply the day that policy changes — so the form
 * states both and lets neither stand for the other.
 */
export function redemptionChargeNote(currency: CurrencyCode): string | null {
  if (currency === "GOLD") return null;
  if (currency === "GREEN") return REDEMPTION_NOTE_GREEN;
  return REDEMPTION_NOTE_DEBIT_LEG;
}

const REDEMPTION_NOTE_GREEN =
  "Game redemptions are not charged a fee under the current settlement policy, so this rate is stored but nothing is deducted yet. It is the number that would apply the day that changes.";

const REDEMPTION_NOTE_DEBIT_LEG =
  "Redemptions pay out, and a payout never moves this currency, so a redemption fee set here is never charged. Set it on the currency the payout is credited in instead.";

/**
 * The sentence the User overrides page carries.
 *
 * A user override is the one fee row SM-510 did not key by currency, and
 * saying so is the difference between a page that looks inconsistent
 * with Fee policies and one that explains why it is.
 *
 * One idea per sentence so the localized content bridge can extract it
 * (the SM-354 note in DESIGN.md), and no ALL-CAPS currency codes: the
 * names an operator configured are what the rest of the page shows.
 */
export const USER_OVERRIDE_CURRENCY_NOTE =
  "User overrides are one rate and are not split by currency, unlike fee policies. They replace the market rate on Cash fills; a Game fill is charged the policy rate for its own currency instead.";

/**
 * Whether a market can be charged in this currency at all — the guard
 * behind the create form's market and category pickers (AC 8).
 *
 * Fail-OPEN on an unread reach: a market whose books this page has not
 * loaded stays offerable. Refusing it would answer "can this market
 * charge a Red fee?" with "no" on the strength of not having looked, and
 * the server enforces the same rule anyway.
 */
export function reachAllowsCurrency(
  reach: FeeReach,
  currency: CurrencyCode | undefined,
): boolean {
  if (!currency) return true;
  if (!reach.known) return true;
  return reach.currencies.includes(currency);
}
